import { useConversationUuid } from '~/hooks'
import { cm, isCampaignUuid } from '~/utils/utils'
import { CurieMarkdown } from '../../markdown'
import { CampaignContextBlock, CampaignContextBlockProps } from './CampaignContextBlock'
import { CampaignDividerBlock, CampaignDividerBlockProps } from './CampaignDividerBlock'
import { CampaignErrorBlock, CampaignErrorBlockProps } from './CampaignErrorBlock'
import { RadioGroupBlock, RadioGroupBlockProps } from './CampaignRadioBlock'
import { CampaignShimmerBlock } from './CampaignShimmerBlock'
import { CampaignTaskBlock, CampaignTaskBlockProps } from './CampaignTaskBlock'
import { CampaignTimelineBlock, CampaignTimelineBlockProps } from './CampaignTimelineBlock'

interface CampaignBlockProps {
  block: Block
  className?: string
  childrenClassName?: string
  index: number
  totalBlock?: number
}

export function CampaignBlock({ block, className, childrenClassName, index, totalBlock }: CampaignBlockProps) {
  const uuid = useConversationUuid()
  const childLength = block.children?.length
  function renderBlock(block: Block) {
    switch (block.type) {
      case 'image':
        return (
          <CurieMarkdown
            key={block.id}
            text={`![image](data:image/png;base64,${block.text?.text})`}
          />
        )
      case 'pr':
        return (
          <CurieMarkdown
            key={block.id}
            text={`[${block.text?.text}](${block.text?.text})`}
            className='pl-0'
          />
        )
      case 'task': {
        const isCampaign = isCampaignUuid(uuid)
        return isCampaign ? null : (
          <CampaignTaskBlock
            key={block.id}
            children={block.children as Block[]}
            fields={block.fields as CampaignTaskBlockProps['fields']}
          />
        )
      }
      case 'context':
        return (
          <CampaignContextBlock
            key={block.id}
            text={block.text}
            fields={block.fields as CampaignContextBlockProps['fields']}
          />
        )
      case 'divider':
        return <CampaignDividerBlock key={block.id} fields={block.fields as CampaignDividerBlockProps['fields']} />
      case 'single-select':
        return (
          <RadioGroupBlock key={block.id} fields={block.fields as RadioGroupBlockProps['fields']} />
        )
      case 'error':
        return (
          <CampaignErrorBlock
            key={block.id}
            text={block.text}
            fields={block.fields as CampaignErrorBlockProps['fields']}
          />
        )
      case 'shimmer':
        return <CampaignShimmerBlock key={block.id} value={block.value} />
      default:
        return (
          <CurieMarkdown
            key={block.id}
            text={block.text?.text || ''}
            className={cm('pl-[40px]', childrenClassName)}
          />
        )
    }
  }

  if (block.type === 'timeline') {
    return (
      <CampaignTimelineBlock
        key={block.id}
        fields={block.fields as CampaignTimelineBlockProps['fields']}
        children={block.children as Block[]}
        totalBlock={totalBlock || 1}
      />
    )
  }

  return (
    <div className={className}>
      {renderBlock(block)}
      <div className={`block-${block.id}-children`}>
        {Array.isArray(block.children)
          ? block.children.map((block, index) => {
              return (
                <CampaignBlock
                  key={block.id}
                  block={block}
                  className={childrenClassName}
                  index={index}
                  totalBlock={childLength}
                />
              )
            })
          : null}
      </div>
    </div>
  )
}
